export interface RouteDefinition {
    path: string;
    name: string;
}

export interface RouterConfig {
    routes: RouteDefinition[];
    notFoundPath?: string;
}

export interface RouteMatch {
    route: RouteDefinition;
    path: string;
    params: Record<string, string>;
}

export type NavigationCallback = (match: RouteMatch) => void;

class Router {
    private routes: RouteDefinition[] = [];
    private currentRoute: string = '';
    private currentPath: string = '/';
    private currentParams: Record<string, string> = {};
    private navigationCallback: NavigationCallback | null = null;
    private notFoundPath: string = '/404';

    constructor(config: RouterConfig) {
        this.routes = config.routes;
        this.notFoundPath = config.notFoundPath || '/404';
        
        if (typeof window !== 'undefined') {
            window.addEventListener('popstate', this.handlePopState.bind(this));
            this.handlePopState();
        }
    }

    onNavigate(callback: NavigationCallback) {
        this.navigationCallback = callback;
        const activeMatch = this.resolveRoute(this.currentPath);
        if (activeMatch) {
            this.navigationCallback(activeMatch);
        }
    }

    private handlePopState() {
        const path = window.location.pathname || '/';
        this.navigate(path, false);
    }

    navigate(path: string, pushState: boolean = true) {
        const targetPath = this.normalizePath(path);
        const match = this.resolveRoute(targetPath);
        if (!match) {
            if (targetPath !== this.notFoundPath) {
                this.navigate(this.notFoundPath, true);
            } else {
                const notFoundMatch = this.resolveRoute(this.notFoundPath);
                if (notFoundMatch) {
                    this.loadRoute(notFoundMatch, pushState);
                }
            }
            return;
        }
        this.loadRoute(match, pushState);
    }

    private normalizePath(path: string): string {
        if (!path || path === '/') {
            return '/';
        }
        return path.replace(/\/$/, '') || '/';
    }

    private resolveRoute(path: string): RouteMatch | undefined {
        const normalizedPath = this.normalizePath(path);

        for (const route of this.routes) {
            if (route.path === normalizedPath) {
                return {
                    route,
                    path: normalizedPath,
                    params: {}
                };
            }
            if (route.path.includes(':')) {
                const paramNames: string[] = [];
                const regexPath = route.path.replace(/:([^/]+)/g, (_, key) => {
                    paramNames.push(key);
                    return '([^/]+)';
                });
                const regex = new RegExp(`^${regexPath}$`);
                const result = regex.exec(normalizedPath);
                if (!result) {
                    continue;
                }
                const params: Record<string, string> = {};
                for (let i = 0; i < paramNames.length; i++) {
                    const key = paramNames[i];
                    const value = result[i + 1];
                    if (key && value !== undefined) {
                        params[key] = decodeURIComponent(value);
                    }
                }
                return {
                    route,
                    path: normalizedPath,
                    params
                };
            }
        }
        return undefined;
    }

    private loadRoute(match: RouteMatch, pushState: boolean) {
        if (pushState) {
            window.history.pushState({}, '', match.path);
        }

        this.currentRoute = match.route.name;
        this.currentPath = match.path;
        this.currentParams = { ...match.params };

        if (this.navigationCallback) {
            this.navigationCallback(match);
        }
        this.dispatchEvent(match);
    }

    private dispatchEvent(match: RouteMatch) {
        const event = new CustomEvent('route-loaded', {
            detail: {
                route: match.route.name,
                path: match.path,
                params: match.params
            },
            bubbles: true,
            composed: true
        });
        document.dispatchEvent(event);
    }

    getCurrentRoute(): string {
        return this.currentRoute;
    }

    getCurrentPath(): string {
        return this.currentPath;
    }

    getCurrentParams(): Record<string, string> {
        return { ...this.currentParams };
    }

    getRoute(name: string): RouteDefinition | undefined {
        return this.routes.find(r => r.name === name);
    }
}

let routerInstance: Router | null = null;

export function initRouter(config: RouterConfig): Router {
    routerInstance = new Router(config);
    return routerInstance;
}

export function getRouter(): Router | null {
    return routerInstance;
}

export function navigateTo(path: string) {
    if (routerInstance) {
        routerInstance.navigate(path);
    }
}
